import { Button, Switch } from '@/components';
import { useBreadcrumbs, useConfirm } from '@/hooks';
import { trans } from '@/i18n';
import { ROUTE_PATH } from '@/routes';
import { NoteService } from '@/services/note';
import { NotePostStatus } from '@/types/enums';
import { INotePostFilter, INotePosts, INotePostsPage } from '@/types/notePost';
import { images, showError } from '@/utils';
import { Box, Flex, Grid, Image, Stack, Text } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GiArchiveResearch } from 'react-icons/gi';
import { EmptyState } from '@/components/ui';
import { SCROLL_NEAR_BOTTOM_THRESHOLD } from '@/constants/common';

// Initial query note post
const initialQuery: INotePostFilter = {
  page: 1,
  size: 16,
  orderKey: 'publishedAt',
  orderBy: 'desc',
};

function NoteArticlePage() {
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { toggleConfirm } = useConfirm();

  const sv = new NoteService();

  const [notePosts, setNotesPosts] = useState<INotePostsPage>({
    data: [],
    loading: false,
    query: initialQuery,
  });
  const [totalPages, setTotalPages] = useState<number>(0);
  const queryRef = useRef<INotePostFilter>(initialQuery);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleCheckedSwitch = async (id: string, checked: boolean) => {
    if (!checked) {
      toggleConfirm({
        title: 'note記事非表示',
        message: 'この記事を非表示にしてもよろしいでしょうか？',
        okText: 'はい',
        cancelText: 'いいえ',
        onOk: async function () {
          await sv.updateNotePost(id, { status: NotePostStatus.REJECTED });
          setNotesPosts((prev) => ({
            ...prev,
            data: prev.data.map((p) => ({
              ...p,
              status: p.id === id ? NotePostStatus.REJECTED : p.status,
            })),
          }));
          return;
        },
      });
      return;
    }

    await sv.updateNotePost(id, { status: NotePostStatus.APPROVED });
    setNotesPosts((prev) => ({
      ...prev,
      data: prev.data.map((p) => ({
        ...p,
        status: p.id === id ? NotePostStatus.APPROVED : p.status,
      })),
    }));
  };

  const stripImages = (html: string) => {
    return html?.replace(/<img[^>]*>/g, '') || '';
  };

  const getNotePosts = useCallback(async (params: INotePostFilter, append = false) => {
    try {
      setNotesPosts((prev) => ({ ...prev, loading: true }));
      queryRef.current = params;
      const response: INotePosts = await sv.getNoteArticles(params);
      const newData = response?.notePosts || [];
      setTotalPages(response?.totalPages || 0);
      setNotesPosts((prev) => ({
        ...prev,
        data: append ? [...prev.data, ...newData] : newData,
        query: params,
        loading: false,
      }));
    } catch (error) {
      showError(error);
      setNotesPosts((prev) => ({ ...prev, loading: false }));
      navigate(ROUTE_PATH.NOTE);
    }
  }, []);

  const handleLoadMore = useCallback(() => {
    if (notePosts.loading) return;

    const currentPage = queryRef.current.page || 1;
    if (totalPages > 0 && currentPage >= totalPages) return;

    const nextPage = currentPage + 1;
    const nextQuery = {
      ...queryRef.current,
      page: nextPage,
    };

    getNotePosts(nextQuery, true);
  }, [notePosts.loading, totalPages]);

  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;

    const isNearBottom = scrollTop + clientHeight >= scrollHeight - SCROLL_NEAR_BOTTOM_THRESHOLD;

    if (isNearBottom) {
      handleLoadMore();
    }
  }, [handleLoadMore]);

  useEffect(() => {
    getNotePosts(
      {
        ...notePosts.query,
        page: 1,
      },
      false
    );
  }, []);

  useEffect(() => {
    setBreadcrumbs(ROUTE_PATH.NOTE, [
      { name: trans('menu.note'), path: ROUTE_PATH.NOTE },
      { name: trans('menu.articleList'), subname: trans('menu.noteArticle') },
    ]);
  }, [notePosts]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  return (
    <Box
      width="full"
      alignContent="center"
      ref={containerRef}
      overflowY="auto"
      maxH="calc(100vh - 120px)"
    >
      <Box className="box-create-content box-note-edit" w="98%">
        <Flex align="center">
          <div className="st-title" color="#2D3748">
            {trans('note.txtNoteList')}
          </div>
        </Flex>
        {
          !notePosts.loading && notePosts.data.length === 0 && (
            <Box className="empty-table">
              <EmptyState icon={<GiArchiveResearch />} description={trans('shared.nodata')} />
            </Box>
          )
        }
        <Grid className='note-article-grid' gap={6} templateColumns="repeat(4, 1fr)">
          {notePosts.data.map((post) => (

            <Box className="box-note-detail">
              {!post.note?.isActive && <div className='box-note-detail-overlay'></div>}
              <Stack direction="column" gap={0} flex={1}>
                <Box className='box-note-detail-image' position="relative" borderRadius="12px" height="191.5px" width="360px">
                  <Image
                    src={post?.thumbnailUrl ?? images.noImage}
                    alt={post.title}
                    borderRadius="12px"
                    height="191.5px"
                    objectFit="cover"
                    width="100%"
                  />
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    borderRadius="12px"
                  />
                </Box>
                <Box className="box-note-detail-content">
                  <Stack direction="column" gap={2} flex={1}>
                    <Text fontSize="10px" color="#A0AEC0" fontWeight="medium">
                      {post.detailLink}
                    </Text>
                    <Text
                      fontSize="18px"
                      fontWeight="bold"
                      color="#2D3748"
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {post.title}
                    </Text>
                    <Text
                      fontSize="12px"
                      color="#A0AEC0"
                      lineHeight="150%"
                      letterSpacing="0"
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                      dangerouslySetInnerHTML={{ __html: stripImages(post.description) }}
                    />
                  </Stack>
                  <Flex justifyContent="space-between" mt="10px">
                    <Switch
                      className="note-page-switch"
                      key={post.id}
                      checked={post.status === NotePostStatus.APPROVED ? true : false}
                      onChange={(e: any) => handleCheckedSwitch(post.id, e.target.checked)}
                    >
                      {trans('note.noteExpress')}
                    </Switch>
                    <a href={post.detailLink} target="_blank">
                      <Button
                        size="sm"
                        variant="outline"
                        width="107.5px"
                        height="35px"
                        borderColor="#4FD1C5"
                        color="#4FD1C5"
                        fontWeight="bold"
                        justifyContent="center"
                        _hover={{
                          bg: '#4FD1C5',
                          color: 'white',
                        }}
                      >
                        {trans('note.openPage')}
                      </Button>
                    </a>
                  </Flex>
                </Box>
              </Stack>
            </Box>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}

export default NoteArticlePage;
